"use client";

import { useEffect, useRef } from "react";

import { Box, BoxProps } from "@mantine/core";
import clsx from "clsx";

import classes from "./PixelField.module.css";

export interface PixelFieldProps extends BoxProps {
  /** Grid pitch in px; each square is drawn slightly smaller to leave a gutter. */
  cellSize?: number;
  /** Fraction of cells lit at any moment (0..1). */
  density?: number;
  /** CSS colors squares pick from. var(--mantine-*) references are resolved at mount. */
  colors?: readonly string[];
  /** Seconds for one square's fade-in / fade-out cycle. */
  cycleSeconds?: number;
  /** Edge the field fades out toward, for blending into adjacent content. */
  fadeTo?: "none" | "left" | "right" | "top" | "bottom";
  /** Deterministic shuffle seed; two fields with different seeds animate differently. */
  seed?: number;
}

/** Gutter between squares, in px. */
const CELL_GAP = 2;

/** Peak opacity of a lit square; keeps the field decorative, never loud. */
const MAX_ALPHA = 0.9;

/** Default palette: the brand's cool family at low saturation. */
const DEFAULT_COLORS: readonly string[] = [
  "var(--mantine-color-blue-2)",
  "var(--mantine-color-blue-3)",
  "var(--mantine-color-cyan-2)",
  "var(--mantine-color-cyan-3)",
  "var(--mantine-color-teal-2)",
];

/** Integer hash → [0, 1). Deterministic so SSR/CSR and reruns agree. */
function hash01(n: number, seed: number): number {
  let h = (n ^ Math.imul(seed + 1, 0x9e3779b9)) >>> 0;
  h = Math.imul(h ^ (h >>> 16), 2246822507);
  h = Math.imul(h ^ (h >>> 13), 3266489909);
  h ^= h >>> 16;

  return (h >>> 0) / 4294967296;
}

/** Resolve "var(--name)" against the element so canvas fillStyle gets a real color. */
function resolveColor(raw: string, el: HTMLElement): string {
  const match = raw.match(/^var\((--[\w-]+)\)$/);
  if (!match) return raw;

  return getComputedStyle(el).getPropertyValue(match[1]).trim() || "transparent";
}

/**
 * A field of square pixels that slowly twinkle: each cycle, a sparse random
 * subset fades in, holds, and fades out in a palette color. The system-diagram
 * counterpart to DottedBackground, for moments that should feel alive.
 * Purely decorative: hidden from the accessibility tree, static under
 * prefers-reduced-motion.
 */
export function PixelField({
  cellSize = 14,
  density = 0.08,
  colors = DEFAULT_COLORS,
  cycleSeconds = 3,
  fadeTo = "none",
  seed = 1,
  className,
  ...props
}: PixelFieldProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const palette = colors.map((color) => resolveColor(color, canvas));
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    let width = 0;
    let height = 0;
    let rafId = 0;

    const draw = (timeMs: number) => {
      ctx.clearRect(0, 0, width, height);

      const cols = Math.ceil(width / cellSize);
      const rows = Math.ceil(height / cellSize);
      const time = timeMs / 1000;

      for (let row = 0; row < rows; row++) {
        for (let col = 0; col < cols; col++) {
          const cell = row * cols + col;

          // Per-cell rhythm: period and phase vary so cycles never sync up.
          const period = cycleSeconds * (0.6 + hash01(cell * 7 + 1, seed) * 0.8);
          const phase = hash01(cell * 7 + 2, seed) * period;
          const cycle = Math.floor((time + phase) / period);
          const frac = (time + phase) / period - cycle;

          // Each cycle, the cell independently decides whether to light up,
          // so squares appear and disappear across the grid over time.
          if (hash01(cell * 7 + cycle * 31 + 3, seed) >= density) continue;

          let alpha = Math.sin(frac * Math.PI) * MAX_ALPHA;

          if (fadeTo !== "none") {
            const along = {
              left: 1 - (col + 0.5) / cols,
              right: (col + 0.5) / cols,
              top: 1 - (row + 0.5) / rows,
              bottom: (row + 0.5) / rows,
            }[fadeTo];

            // Squared falloff so the edge dissolves instead of hard-stopping.
            alpha *= (1 - along) ** 2;
          }

          if (alpha <= 0.01) continue;

          const color =
            palette[Math.floor(hash01(cell * 7 + cycle * 31 + 4, seed) * palette.length)];

          ctx.globalAlpha = alpha;
          ctx.fillStyle = color;
          ctx.fillRect(col * cellSize, row * cellSize, cellSize - CELL_GAP, cellSize - CELL_GAP);
        }
      }

      ctx.globalAlpha = 1;
    };

    const tick = (timeMs: number) => {
      draw(timeMs);
      rafId = requestAnimationFrame(tick);
    };

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;

      width = rect.width;
      height = rect.height;
      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      if (reduceMotion) draw(0);
    };

    const observer = new ResizeObserver(resize);
    observer.observe(canvas);
    resize();

    if (!reduceMotion) rafId = requestAnimationFrame(tick);

    return () => {
      observer.disconnect();
      cancelAnimationFrame(rafId);
    };
  }, [cellSize, density, colors, cycleSeconds, fadeTo, seed]);

  return (
    <Box className={clsx(classes.root, className)} aria-hidden="true" {...props}>
      <canvas ref={canvasRef} className={classes.canvas} />
    </Box>
  );
}
